/**
 * Parse a simulation's `intro_text` into the shape WelcomeScreen expects:
 *   - protagonist name + role (extracted from "You are X, Y" first line)
 *   - three briefing cards (Situation / Challenge / Your Role)
 *
 * Tolerates both `## Heading` and `**Heading**` / `**Heading:**` section markers.
 * Ported from NeuArena's SimPlayerPage so NeuSim renders the same briefing UX.
 */
export interface SimIntroSection {
  title: string;
  body: string;
  color: "cyan" | "amber" | "violet";
}

export interface ParsedSimIntro {
  name: string;
  role: string;
  sections: SimIntroSection[];
}

export function parseSimIntro(text: string): ParsedSimIntro {
  let name = "";
  let role = "";
  const sections: SimIntroSection[] = [];

  const m = text.match(/\*{0,2}(?:#{1,3}\s*)?You are\s+([A-Z][a-zA-Zé'\- ]+?)(?:,\s*([^\n*]+?))?\*{0,2}[\n\r]/);
  if (m && m[1] !== undefined) {
    name = m[1].trim();
    if (m[2] !== undefined) {
      role = m[2].trim();
    } else {
      const after = text.slice(text.indexOf(m[0]) + m[0].length);
      const line = after.split("\n").find((l) => {
        const t = l.replace(/[*#]/g, "").trim();
        return t.length > 4 && t.length < 120;
      });
      if (line) role = line.replace(/[*#]/g, "").trim();
    }
  }

  const sdef: { re: RegExp; title: string; color: "cyan" | "amber" | "violet" }[] = [
    {
      re: /(?:^|\n)\s*(?:#{1,3}\s*|\*{1,3}\s*)(?:The\s+)?Situation[^*\n]*\*{0,3}[:\s]*\n+([\s\S]+?)(?=\n\s*#{1,3}|\n\s*\*{1,3}[A-Z]|$)/i,
      title: "🏢 The Situation",
      color: "cyan",
    },
    {
      re: /(?:^|\n)\s*(?:#{1,3}\s*|\*{1,3}\s*)(?:Your\s+)?Challenge[^*\n]*\*{0,3}[:\s]*\n+([\s\S]+?)(?=\n\s*#{1,3}|\n\s*\*{1,3}[A-Z]|$)/i,
      title: "⚡ Your Challenge",
      color: "amber",
    },
    {
      re: /(?:^|\n)\s*(?:#{1,3}\s*|\*{1,3}\s*)Your\s+Role[^*\n]*\*{0,3}[:\s]*\n+([\s\S]+?)(?=\n\s*#{1,3}|\n\s*\*{1,3}[A-Z]|$)/i,
      title: "🎯 Your Role",
      color: "violet",
    },
  ];

  for (const { re, title, color } of sdef) {
    const sm = text.match(re);
    const body = sm?.[1]?.trim();
    if (body !== undefined && body.length > 20) {
      // Up to ~2000 chars per section — comfortable for a paragraph of
      // substantial brief content. The WelcomeScreen briefing card scrolls
      // naturally if it ever overflows. Earlier 600-char limit was cutting
      // longer briefings off mid-sentence.
      sections.push({ title, body: body.replace(/\n+/g, " ").slice(0, 2000), color });
    }
  }

  // Fallback: if no sections parsed, drop everything after the name line into a single Situation card.
  if (sections.length === 0 && text.trim().length > 50) {
    const nameEnd = m ? text.indexOf(m[0]) + m[0].length : 0;
    const body = text.slice(nameEnd).replace(/[*#]+/g, "").replace(/\n+/g, " ").trim();
    if (body.length > 30) {
      sections.push({ title: "🏢 The Situation", body: body.slice(0, 2200), color: "cyan" });
    }
  }

  return { name, role, sections };
}
